<script setup lang="ts">
useHead({
  htmlAttrs: {
    lang: "en",
  },
  title:
    "We keep upgrading ourselves. Current capabilities like Laravel, VueJS, SendGrid, Stripe etc",
  meta: [
    {
      name: "description",
      content:
        "We keep upgrading ourselves. Current capabilities like Laravel, VueJS, SendGrid, Stripe etc",
    },
    {
      name: "keywords",
      content: "capabilities,laravel,vuejs,bootstrap,mailgun",
    },
  ],
});

import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useDebounce } from "@vueuse/core";
import WrDotted from "~/components/atoms/icons/WrDotted.vue";
import WrInputSearch from "~/components/atoms/icons/WrInputSearch.vue";

const checkedCategories = ref<string[]>([]);
const integrationCards = [
  {
    title: "Laravel",
    description: "Open-source object-oriented model-view-controller for PHP.",
    icon: "/images/laravel.webp",
    link: "/capabilities/laravel-development-company",
    iconPosition: "bg-[position:6px_-300px]",
    tags: ["REST API", "Authentication", "Database", "Security", "Logging"],
  },
  {
    title: "Vue.js",
    description: "Easily build astounding single page apps much faster.",
    icon: "/images/vuejs.webp",
    link: "/capabilities/vuejs-development-company",
    iconPosition: "bg-[position:6px_-300px]",
    tags: ["REST API", "WebSocket"],
  },
  {
    title: "MOR API",
    description: "Cost-Effective, High-Quality MOR API Integration Services",
    icon: "/images/morapi.webp",
    link: "/capabilities/mor-api-development-company",
    iconPosition: "bg-[position:6px_-300px]",
    tags: ["REST API", "Authentication"],
  },
  {
    title: "Facebook API",
    description: "Get the power of Facebook in your website & application.",
    icon: "/images/facebook.webp",
    link: "/capabilities/facebook-api-integration-company",
    iconPosition: "bg-[position:6px_-300px]",
    tags: ["OAuth", "Authentication", "REST API"],
  },
  {
    title: "PayPal",
    description: "Quick, secure payments via account, bank, or card.",
    icon: "/images/paypal.webp",
    link: "/capabilities/paypal-integration-services",
    iconPosition: "bg-[position:6px_-300px]",
    tags: ["Security", "Authentication", "REST API"],
  },
  {
    title: "GOOGLE CALENDAR API",
    description: "Effortless event tracking on your site.",
    icon: "/images/googlecalendar.webp",
    link: "/capabilities/google-calendar-api-integration-services",
    iconPosition: "bg-[position:6px_-300px]",
    tags: ["REST API", "OAuth"],
  },
  {
    title: "Highcharts",
    description: "Add interactive, responsive charts to website or web app.",
    icon: "/images/highcharts.webp",
    link: "/capabilities/highcharts-api-integration-solutions",
    iconPosition: "bg-[position:6px_-300px]",
    tags: ["Observability"],
  },
  {
    title: "SendGrid",
    description: "Customize, measure and automate your email campaign.",
    icon: "/images/sendgrid.webp",
    link: "/capabilities/sendgrid",
    iconPosition: "bg-[position:6px_-300px]",
    tags: ["SMTP", "Monitoring"],
  },
  {
    title: "Ajax",
    description:
      "Enhance user experience with faster, more responsive interactions.",
    icon: "/images/ajax.webp",
    link: "/capabilities/ajax-development-company",
    iconPosition: "bg-[position:6px_-300px]",
    tags: ["REST API", "WebSocket"],
  },
  {
    title: "AdonisJs",
    description:
      "Heavily inspired by Laravel. Explore power of Laravel on NodeJS.",
    icon: "/images/adonisjs.webp",
    link: "/capabilities/adonisjs-nodejs-development-company",
    iconPosition: "bg-[position:6px_-300px]",
    tags: ["REST API", "Authentication", "Database", "Security"],
  },
  {
    title: "MAILGUN",
    description:
      "Send, receive, and track emails. Enjoy fast delivery & smooth delivery.",
    icon: "/images/mailgun.webp",
    link: "/capabilities/mailgun-api-integration-company",
    iconPosition: "bg-[position:6px_-300px]",
    tags: ["SMTP", "Monitoring", "Logging"],
  },
  {
    title: "Stripe",
    description:
      "Create subscriptions/accept payments easily in your eCommerce site.",
    icon: "/images/stripe.webp",
    link: "/capabilities/stripe-integration-company",
    iconPosition: "bg-[position:6px_-300px]",
    tags: ["REST API", "Security", "Authentication"],
  },
  {
    title: "Slack",
    description: "Streamlined communication for teams and individuals.",
    icon: "/images/slack.webp",
    link: "/capabilities/slack-integration-company",
    iconPosition: "bg-[position:6px_-300px]",
    tags: ["WebSocket", "Observability", "Monitoring"],
  },
  {
    title: "Push notification",
    description:
      "Drive traffic by providing valuable updates to your customers instantly.",
    icon: "/images/pushnotification.webp",
    link: "/capabilities/push-notifications-integration-company",
    iconPosition: "bg-[position:6px_-300px]",
    tags: ["WebSocket", "Monitoring"],
  },
  {
    title: "Pusher",
    description:
      "Scalable, real-time push notifications for seamless engagement.",
    icon: "/images/pusher.webp",
    link: "/capabilities/pusher-integration-company",
    iconPosition: "bg-[position:6px_-300px]",
    tags: ["WebSocket", "Monitoring"],
  },
  {
    title: "MongoDb",
    description: "For large data transactions and scalable model design.",
    icon: "/images/mongodb.webp",
    link: "/capabilities/mongodb-development-company",
    iconPosition: "bg-[position:6px_-300px]",
    tags: ["Database", "Caching", "Logging"],
  },
  {
    title: "2 Checkout",
    description:
      "Boost sales with localized payment options for global buyers.",
    icon: "/images/2checkout.webp",
    link: "/capabilities/2checkout-payment-gateway-integration",
    iconPosition: "bg-[position:6px_-300px]",
    tags: ["REST API", "Security"],
  },
];

const search = ref("");
const debouncedSearch = useDebounce(search, 250);

let fuseWorker: Worker | null = null;
const workerResults = ref([...integrationCards]);
const workerReady = ref(false);

onMounted(() => {
  fuseWorker = new Worker(new URL("./fuseWorker.js", import.meta.url), {
    type: "module",
  });
  fuseWorker.onmessage = (e: MessageEvent) => {
    const { type, items } = e.data;
    if (type === "ready") {
      workerReady.value = true;
    }
    if (type === "results") {
      workerResults.value = items;
    }
  };
  fuseWorker.postMessage({
    type: "init",
    payload: { items: integrationCards },
  });
});

onBeforeUnmount(() => {
  if (fuseWorker) {
    fuseWorker.terminate();
    fuseWorker = null;
  }
});
const searchInput = ref(null);

const handleFocusChange = (value: any) => {
  if (value) {
    searchInput.value.scrollIntoView({ behavior: "smooth", block: "center" });
    searchInput.value.focus();
  }
};

const categoryCountMap = integrationCards.reduce((acc, card) => {
  card.tags.forEach((tag) => {
    acc[tag] = (acc[tag] || 0) + 1;
  });
  return acc;
}, {} as Record<string, number>);

const allCategories = [
  "SMTP",
  "IMAP",
  "Observability",
  "REST API",
  "GraphQL",
  "WebSocket",
  "OAuth",
  "Authentication",
  "Database",
  "Caching",
  "Monitoring",
  "Logging",
  "Security",
].map((cat) => ({
  name: cat,
  checked: false,
  count: categoryCountMap[cat] || 0,
}));

watch(debouncedSearch, (val) => {
  if (!workerReady.value || !fuseWorker) return;
  if (!val.trim()) {
    workerResults.value = [...integrationCards];
  } else {
    fuseWorker.postMessage({ type: "search", payload: { query: val } });
  }
});

const filteredServices = computed(() => {
  let filtered = [...workerResults.value];
  if (checkedCategories.value.length > 0) {
    filtered = filtered.filter((card) =>
      card.tags.some((tag) => checkedCategories.value.includes(tag))
    );
  }
  return filtered;
});

const highlightMatches = (text: string, searchTerm: string) => {
  if (!searchTerm.trim()) return text;
  const escapedSearch = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escapedSearch})`, "gi");
  return text.replace(regex, '<span class="search-highlight">$1</span>');
};

const handleFilterCategory = (filteredList: typeof allCategories) => {
  checkedCategories.value = filteredList
    .filter((cat) => cat.checked)
    .map((cat) => cat.name);
};

const currentPage = ref(1);
watch(search, () => {
  if (search.value.length === 0) {
    currentPage.value = 1;
  }
});
const itemsPerPage = 6;

const visibleServices = computed(() => {
  const updatedServices = [...filteredServices.value];
  return updatedServices.slice(0, currentPage.value * itemsPerPage);
});

const hasMoreServices = computed(() => {
  return visibleServices.value.length < filteredServices.value.length;
});

const loadMore = () => {
  currentPage.value++;
};

const props = defineProps({
  title: { type: String, default: "Our Services" },
  description: { type: String },
});
</script>

<template>
  <div class="z-10 relative dark:bg-[linear-gradient(109.63deg,#190226_3.67%,#1F0035_83.91%)]">
    <section class="relative z-10 py-10 max-sm:pt-0 overflow-visible grid place-items-center">
      <div class="max-w-screen-xl mx-auto px-4 w-full relative">
        <div>
          <h1 class="mb-1">
            <WrGradientText
              text="Discover the technologies"
              append-class="pb-0 md:pb-2"
            />
          </h1>
          <p class="text-4xl max-lg:text-2xl">powering our latest solutions.</p>
          <p class="lg:text-xl max-lg:text-base mt-4">
          Browse the tools and stacks behine our successfull projects.
          </p>
        </div>
      </div>
    </section>

    <section>
      <div class="max-w-screen-xl mx-auto px-4 py-2 lg:pb-24">
        <!-- <p class="text-xl font-semibold mb-2">Capabilities</p> -->

        <div class="flex max-lg:flex-col-reverse w-full *:rounded-lg gap-4 relative">
          <div class="w-[662px] h-[433px] rounded-full bg-[#C399ED80] blur-[150px] absolute top-0 left-0 dark:hidden">
          </div>
          <div
            class="w-[279px] h-[988px] rounded-[100%] bg-[#FFFFFF] blur-[50px] absolute top-[25%] -right-14 dark:hidden">
          </div>
          <WrFilterCapabilities @categoryFilter="handleFilterCategory" :all-categories="allCategories"
            @toggle-more-categories="handleFocusChange" />
          <div
            class="z-10 relative w-full grow bg-[#F9F9F9] dark:bg-transparent dark:bg-[image:var(--bg-card-capabilities)] border border-[var(--border-card-capabilities)] py-6 lg:py-12 px-6">
            <div class="mb-12">
              <div
                class="relative dark:p-px dark:bg-[linear-gradient(90deg,_#3F1974_0%,_#502C86_100%)] max-w-full rounded-full"
                ref="searchInput">
                <InputText type="text" v-model="search"
                  class="!rounded-full !bg-white dark:!bg-[#2B094D] !bg-none !w-full !border-0 !px-5 !text-base !pr-12"
                  placeholder="Search" />

                <!-- Clear button -->
                <button v-if="search.trim()" @click="search = ''"
                  class="absolute right-4 top-1/2 -translate-y-1/2 size-6 text-gray-500 hover:text-gray-700 dark:text-[#DAC9FF] dark:hover:text-white transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <WrInputSearch v-if="!search.trim()" class="absolute right-4 top-1/2 -translate-y-1/2" />
              </div>
            </div>
            <!-- Results Grid -->
            <div class="grid sm:grid-cols-2 gap-4" v-if="visibleServices.length > 0">
              <div v-for="card in visibleServices"
                class="relative p-px bg-[#5F1F981A] rounded-lg dark:bg-[linear-gradient(91.95deg,_#5F1F98_4.5%,_#55148D_96.91%)] dark:after:content-[''] dark:after:w-[150px] dark:after:h-0.5 dark:after:bg-[linear-gradient(90deg,_rgba(32,13,46,0)_0.34%,_#9C59D1_52.2%,_rgba(32,13,46,0)_96.97%)] dark:after:absolute dark:after:top-0 dark:after:left-6">
                <div
                  class="h-full bg-[#FFFFFFB2] dark:bg-transparent dark:bg-[linear-gradient(333.83deg,_rgba(44,6,76)_4.12%,_rgba(63,0,119)_73.28%,_rgba(103,39,147)_113.65%)] rounded-lg px-5 py-6">
                  <NuxtLink :aria-label="card.title" :to="card.link" class="absolute inset-0 z-20" />
                  <WrDotted
                    class="absolute top-0 dark:bottom-1 dark:top-auto left-3 text-[#f1f1f1] dark:text-[#310856]" />
                  <div class="relative z-10">
                    <div
                      class="size-16 rounded-md overflow-hidden grid place-content-center mb-2 bg-white border border-[#DDDDDD] dark:border-0">
                      <div v-if="card.iconPosition !== 'bg-[position:6px_-300px]'" :class="[
                        'bg-[url(/images/capabilities-icons.webp)] size-[68px] bg-no-repeat',
                        card.iconPosition
                      ]">
                      </div>

                      <div v-else
                        class="size-16 flex items-center justify-center text-lg md:text-xl font-bold text-white bg-[var(--primary)] rounded-md">
                        {{ card.title.charAt(0) }}
                      </div>
                    </div>

                    <WrGradientText :text="card.title" append-class="text-2xl font-semibold pb-1"></WrGradientText>
                    <p class="tracking-[0.5px]" v-html="highlightMatches(card.description, search)"></p>
                  </div>
                </div>
              </div>
            </div>

            <!-- No results found state -->
            <div v-if="search.trim() && visibleServices.length === 0" class="text-center py-16">
              <div class="mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-20 h-20 text-gray-300 mx-auto" fill="none"
                  viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 class="text-xl font-semibold text-gray-600 mb-3">No capabilities found</h3>
              <p class="text-gray-500 mb-6">
                No results found for "{{ search }}". Try searching with different keywords.
              </p>
              <WrButton container-class="mx-auto" @click="search = ''">
                Show All Technologies
              </WrButton>
            </div>

            <!-- View More Button -->
            <div class="text-center mt-10" v-if="hasMoreServices && visibleServices.length > 0">
              <button @click="loadMore"
                class="mx-auto size-14 rounded-full border-2 border-gray-400 flex justify-center items-center *:rounded-full">
                <span class="size-1 bg-red-400 mr-1"></span>
                <span class="size-1 bg-yellow-400 mr-1"></span>
                <span class="size-1 bg-green-400"></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section>
      <div class="max-w-screen-xl mx-auto py-10 lg:pb-24">
        <h2 class="text-center px-4">
          <WrGradientText text="Trusted for excellence and efficiency" />
        </h2>
        <p class="px-4 text-lg text-[var(--foreground)] mb-10 text-center tracking-[0.5px]">
          Webreinvent is trusted by leading organisations to enhance their
          development and operational efficiency.
        </p>
        <!-- Carousel Section -->

        <WrTestimonialCardCarousel />

        <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-2">View All</WrButton>
      </div>
    </section>

    <section class="max-w-screen-lg mx-auto mt-10">
      <WrResultSection />
    </section>
  </div>
</template>
